<template>
  <div>
    <h2 class="text-2xl font-bold text-slate-800 mb-6">Análisis de Costos</h2>

    <!-- Filtros -->
    <div class="bg-white p-6 rounded-lg shadow-sm border border-slate-200 mb-6">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Selector de categoría -->
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">
            Categoría (Opcional)
          </label>
          <select v-model="selectedCategory" class="input-field">
            <option value="">Todas las categorías</option>
            <option 
              v-for="[id, label] in Object.entries(categoryStore.categories)" 
              :key="id" 
              :value="id"
            >
              {{ label }}
            </option>
          </select>
        </div>

        <!-- Buscador de productos -->
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">
            Buscar Productos
          </label>
          <div class="relative">
            <input
              v-model="searchTerm"
              type="text"
              class="input-field"
              placeholder="Buscar producto..."
            />
            <div 
              v-if="filteredProducts.length > 0 && searchTerm"
              class="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-60 overflow-y-auto"
            >
              <button
                v-for="product in filteredProducts"
                :key="product.id"
                @click="selectProduct(product)"
                class="w-full text-left px-4 py-2 hover:bg-slate-50 transition-colors"
              >
                {{ product.Nombre }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista de productos seleccionados -->
    <div v-if="selectedProducts.length > 0" class="bg-white p-6 rounded-lg shadow-sm border border-slate-200 mb-6">
      <h3 class="text-lg font-semibold text-slate-800 mb-4">Productos Seleccionados</h3>
      <div class="space-y-3">
        <div 
          v-for="product in selectedProducts" 
          :key="product.id"
          class="flex items-center justify-between bg-slate-50 p-3 rounded-lg"
        >
          <div>
            <p class="font-medium text-slate-800">{{ product.Nombre }}</p>
            <p class="text-sm text-slate-600">
              {{ categoryStore.categories[product.Categoria] }}
            </p>
          </div>
          <button 
            @click="removeProduct(product)"
            class="text-red-600 hover:text-red-800"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Análisis -->
    <div v-if="selectedProducts.length > 0" class="space-y-6">
      <!-- Comparativa de laboratorios -->
      <div class="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
        <h3 class="text-lg font-semibold text-slate-800 mb-4">Comparativa de Laboratorios</h3>
        <div class="overflow-x-auto">
          <table class="table-elegant">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Laboratorio</th>
                <th class="text-right">Precio de Costo</th>
                <th class="text-right">Descuento</th>
                <th class="text-right">Costo Final</th>
                <th class="text-right">Diferencia vs Menor</th>
                <th>Tiempo de Demora</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="product in selectedProducts" :key="product.id">
                <tr 
                  v-for="analysis in getProductAnalysis(product)" 
                  :key="`${product.id}-${analysis.laboratory.id}`"
                  :class="{
                    'bg-green-50': analysis.isCheapest,
                    'bg-red-50': analysis.isExpensive
                  }"
                >
                  <td class="font-medium">{{ product.Nombre }}</td>
                  <td>{{ analysis.laboratory.Nombre }}</td>
                  <td class="text-right">${{ analysis.costPrice.toLocaleString() }}</td>
                  <td class="text-right">
                    {{ analysis.discount > 0 ? `${analysis.discount}%` : '-' }}
                  </td>
                  <td class="text-right font-medium" 
                      :class="analysis.isCheapest ? 'text-green-600' : analysis.isExpensive ? 'text-red-600' : ''">
                    ${{ analysis.finalCost.toLocaleString() }}
                  </td>
                  <td class="text-right">
                    <span v-if="analysis.diffFromCheapest > 0" class="text-red-600">
                      +${{ analysis.diffFromCheapest.toLocaleString() }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td>{{ analysis.deliveryTime }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Gráfico de precios -->
      <div class="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
        <h3 class="text-lg font-semibold text-slate-800 mb-4">Distribución de Costos</h3>
        <div class="h-64">
          <div class="flex h-full items-end space-x-4">
            <div 
              v-for="product in selectedProducts" 
              :key="product.id"
              class="flex-1"
            >
              <div class="text-center mb-2 text-sm font-medium text-slate-700">
                {{ product.Nombre }}
              </div>
              <div class="flex h-full items-end space-x-1">
                <div 
                  v-for="analysis in getProductAnalysis(product)"
                  :key="analysis.laboratory.id"
                  class="flex-1 relative group"
                >
                  <div 
                    class="w-full transition-all duration-200"
                    :class="{
                      'bg-green-500': analysis.isCheapest,
                      'bg-red-500': analysis.isExpensive,
                      'bg-indigo-500': !analysis.isCheapest && !analysis.isExpensive
                    }"
                    :style="{ height: `${(analysis.finalCost / maxCost) * 100}%` }"
                  ></div>
                  <!-- Tooltip -->
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    {{ analysis.laboratory.Nombre }}: ${{ analysis.finalCost.toLocaleString() }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useProductStore, useCategoryStore } from '../../stores';
import type { ProductWithDetails } from '../../types';

const productStore = useProductStore();
const categoryStore = useCategoryStore();

const searchTerm = ref('');
const selectedCategory = ref('');
const selectedProducts = ref<ProductWithDetails[]>([]);

// Filtered and sorted products based on search term and category
const filteredProducts = computed(() => {
  if (!searchTerm.value) return [];
  
  return productStore.products
    .filter(product => {
      const matchesSearch = product.Nombre.toLowerCase().includes(searchTerm.value.toLowerCase());
      const matchesCategory = !selectedCategory.value || product.Categoria === selectedCategory.value;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => a.Nombre.localeCompare(b.Nombre)); // Sort alphabetically
});

interface PriceAnalysis {
  laboratory: {
    id: string;
    Nombre: string;
  };
  costPrice: number;
  discount: number;
  finalCost: number;
  diffFromCheapest: number;
  deliveryTime: string;
  isCheapest: boolean;
  isExpensive: boolean;
}

// Select a product from the dropdown
const selectProduct = (product: ProductWithDetails) => {
  if (!selectedProducts.value.some(p => p.id === product.id)) {
    selectedProducts.value.push(product);
  }
  searchTerm.value = '';
};

// Remove a product from analysis
const removeProduct = (product: ProductWithDetails) => {
  selectedProducts.value = selectedProducts.value.filter(p => p.id !== product.id);
};

// Get analysis for a specific product
const getProductAnalysis = (product: ProductWithDetails): PriceAnalysis[] => {
  const analysisResults: PriceAnalysis[] = product.laboratories.map(lab => {
    const costPrice = lab.details.PrecioDeCosto;
    const discount = lab.laboratory.descuentos?.[product.Categoria] || 0;
    const finalCost = costPrice * (1 - discount / 100);
    
    return {
      laboratory: {
        id: lab.laboratory.id!,
        Nombre: lab.laboratory.Nombre
      },
      costPrice,
      discount,
      finalCost,
      diffFromCheapest: 0,
      deliveryTime: lab.details.TiempoDeDemora,
      isCheapest: false,
      isExpensive: false
    };
  });

  // Find min and max costs
  const minCost = Math.min(...analysisResults.map(a => a.finalCost));
  const maxCost = Math.max(...analysisResults.map(a => a.finalCost));

  // Update differences and flags
  return analysisResults.map(result => ({
    ...result,
    diffFromCheapest: result.finalCost - minCost,
    isCheapest: result.finalCost === minCost,
    isExpensive: result.finalCost === maxCost
  }));
};

// Maximum cost across all products and laboratories
const maxCost = computed(() => {
  return Math.max(
    ...selectedProducts.value.flatMap(product =>
      product.laboratories.map(lab => {
        const discount = lab.laboratory.descuentos?.[product.Categoria] || 0;
        return lab.details.PrecioDeCosto * (1 - discount / 100);
      })
    )
  );
});
</script>